import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

/* ── MUI Components ─────────────────────────────────────────────── */
import {
    Autocomplete,
    Box,
    Button,
    ButtonGroup,
    capitalize,
    ClickAwayListener,
    FormControlLabel,
    Grow,
    IconButton,
    LinearProgress,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    useMediaQuery,
} from '@mui/material';

/* ── MUI Icons ──────────────────────────────────────────────────── */
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LunchDiningIcon from '@mui/icons-material/LunchDining';
import MoreVertIcon from '@mui/icons-material/MoreVert';

/* ── Third-party ────────────────────────────────────────────────── */
import { jsPDF } from 'jspdf';

/* ── App components & utilities ─────────────────────────────────── */
import ConfirmationModal from '../Modals/ConfirmationModal/ConfirmationModal';
import EditDietModal from '../Modals/EditDietModal/EditDietModal';
import AddMealModal from '../Modals/AddMealModal/AddMealModal';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import { useAppContext } from '../../context/AppContext';
import { formatDate, getFullDate } from '../../util';
import './AlternativesDietTable.css';
import { capitalizeFirst } from '../../utils/functions';
import TourButton from '../TourButton/TourButton';
import { getAlternativesDietTableSteps } from '../../tours/alternativesDietTable';

/* ═══════════════════════════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════════════════════════ */

const MEAL_ORDER = [
    'breakfast', 'snack1', 'lunch', 'snack2',
    'dinner', 'snack3', 'preWorkout', 'postWorkout',
];

const ALL_MACROS = ['kcal', 'fats', 'carbs', 'sugars', 'fibers', 'proteins', 'salt'];
const PROPORTIONAL_MACROS = ['kcal', 'fats', 'proteins', 'carbs'];

const buildNumericFieldSx = (appColors) => ({
    width: '64px',
    '& .MuiOutlinedInput-root': {
        '&:hover fieldset': { borderColor: appColors.tertiary },
        '&.Mui-focused fieldset': { borderColor: appColors.secondary },
    },
    '& .MuiInputBase-input': {
        padding: '4px 8px',
        textAlign: 'right',
        fontSize: '0.85rem',
        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
        },
        '&[type=number]': { MozAppearance: 'textfield' },
    },
});

/* ═══════════════════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════════════════ */

/**
 * AlternativesDietTable
 * ─────────────────────
 * Displays an alternatives-format diet plan (day0 = main, day1..N = alternatives).
 * Each food row has an expand toggle that reveals per-food alternative options.
 *
 * Props mirror DietTable for drop-in conditional rendering.
 */
const AlternativesDietTable = ({
    diet,
    editedDiet,
    editing = false,
    onDietChange,
    refreshDietTable,
    onDeleteDiet,
    client,
    dietGeneration,
    showInfoBar = true,
}) => {
    const { t } = useTranslation();
    const { appColors, currentLanguage } = useAppContext();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const tourSteps = useMemo(() => getAlternativesDietTableSteps(t), [t]);


    /* ── Local state ──────────────────────────────────────────────── */
    const [loading, setLoading] = useState(false);
    const [selectedDay, setSelectedDay] = useState(0);

    // Modal flags
    const [showEditDietModal, setShowEditDietModal] = useState(false);
    const [showDeleteDietConfirmationModal, setShowDeleteDietConfirmationModal] = useState(false);
    const [showDeleteMealConfirmationModal, setShowDeleteMealConfirmationModal] = useState(false);
    const [deleteMealConfirmationModalOptions, setDeleteMealConfirmationModalOptions] = useState({ meal: '', day: '' });
    const [showAddMealModal, setShowAddMealModal] = useState(false);

    // Food autocomplete
    const [foodSearchList, setFoodSearchList] = useState([]);
    const [altFoodSearchList, setAltFoodSearchList] = useState([]);
    const [onlyGenerics, setOnlyGenerics] = useState(true);

    // Meal options menu anchors
    const [menuAnchorEls, setMenuAnchorEls] = useState(
        Object.fromEntries(MEAL_ORDER.map((m) => [m, null]))
    );

    // Set of "${dayKey}-${mealName}-${foodIndex}" for expanded food alternatives
    const [expandedFoods, setExpandedFoods] = useState(new Set());

    // Working copy of diet plan (all days)
    const [dietPlan, setDietPlan] = useState({});

    // Frozen snapshot for proportional macro recalculation
    const [baseMacrosFoodProportion, setBaseMacrosFoodProportion] = useState({});

    const numFieldSx = buildNumericFieldSx(appColors);

    /* ═════════════════════════════════════════════════════════════════
       PARSE DIET DATA → LOCAL STATE
       ═════════════════════════════════════════════════════════════════ */

    useEffect(() => {
        const parseDiet = () => {
            const parsedDietPlan = {};

            for (const [day, dailyDiet] of Object.entries(diet.dietPlan)) {
                const parsedDailyDiet = {};
                const dailyTotals = Object.fromEntries(ALL_MACROS.map((m) => [m, 0]));

                MEAL_ORDER.forEach((meal) => {
                    if (!dailyDiet[meal]) return;

                    const mealInfo = dailyDiet[meal];
                    const parsedMealInfo = { ...mealInfo };
                    const mealTotals = Object.fromEntries(ALL_MACROS.map((m) => [m, 0]));

                    parsedMealInfo.foods = mealInfo.foods.map((food) => {
                        ALL_MACROS.forEach((macro) => {
                            mealTotals[macro] += food[macro] ?? 0;
                        });
                        return {
                            ...food,
                            alternatives: Array.isArray(food.alternatives)
                                ? food.alternatives.map((alt) => ({ ...alt }))
                                : [],
                        };
                    });

                    ALL_MACROS.forEach((macro) => {
                        parsedMealInfo[macro] = mealTotals[macro];
                    });

                    parsedDailyDiet[meal] = parsedMealInfo;
                });

                Object.values(parsedDailyDiet).forEach((meal) => {
                    ALL_MACROS.forEach((macro) => {
                        dailyTotals[macro] += meal[macro] ?? 0;
                    });
                });

                parsedDailyDiet.totals = dailyTotals;
                parsedDietPlan[day] = parsedDailyDiet;
            }

            setBaseMacrosFoodProportion(JSON.parse(JSON.stringify(parsedDietPlan)));
            setDietPlan(parsedDietPlan);
        };

        if (!dietGeneration && diet?.dietPlan) {
            parseDiet();
        }
    }, [diet, dietGeneration]);

    /* ── Food search ──────────────────────────────────────────────── */

    const getFoodsFilteredByName = async (foodName) => {
        const url = `${process.env.REACT_APP_BACKEND_URL}/foods/?nameFilter=${foodName}&language=${currentLanguage}${onlyGenerics ? '&onlyGenerics=true' : ''}`;
        const response = await axios.get(url, { withCredentials: true });
        setFoodSearchList(response.data);
    };

    const handleFoodInputChange = (event, newInputValue, reason, day, meal, index) => {
        if (reason !== 'input' && reason !== 'clear') return;

        if (!newInputValue) {
            setFoodSearchList([]);

            const blankFood = {
                name: '', quantity: 0, kcal: 0, fats: 0, proteins: 0,
                carbs: 0, sugars: 0, fibers: 0, salt: 0, alternatives: [],
            };
            const foodListCopy = [...dietPlan[day][meal].foods];
            foodListCopy[index] = blankFood;

            const newDietPlan = { ...dietPlan };
            newDietPlan[day][meal] = { ...newDietPlan[day][meal], foods: foodListCopy };

            ALL_MACROS.forEach((macro) => {
                newDietPlan[day][meal][macro] = foodListCopy.reduce(
                    (sum, f) => sum + (parseFloat(f[macro]) || 0), 0
                );
            });

            const dailyTotals = Object.fromEntries(ALL_MACROS.map((m) => [m, 0]));
            Object.keys(newDietPlan[day]).forEach((key) => {
                if (key === 'totals') return;
                ALL_MACROS.forEach((macro) => {
                    dailyTotals[macro] += parseFloat(newDietPlan[day][key][macro]) || 0;
                });
            });
            newDietPlan[day].totals = dailyTotals;

            setDietPlan(newDietPlan);
            setBaseMacrosFoodProportion((prev) => ({
                ...prev,
                [day]: { ...prev[day], [meal]: { ...prev[day][meal], foods: foodListCopy } },
            }));

            if (onDietChange) onDietChange({ ...editedDiet, dietPlan: newDietPlan });
        } else {
            getFoodsFilteredByName(newInputValue);
        }
    };

    /* ── Day navigation ───────────────────────────────────────────── */

    const handleDayChange = (day) => setSelectedDay(day);

    /* ═════════════════════════════════════════════════════════════════
       MAIN FOOD FIELD CHANGE — PROPORTIONAL MACRO RECALCULATION
       Same logic as DietTable.handleDietMealFoodChange
       Name encoding: "{day}-{meal}-{foodIndex}-{field}"
       ═════════════════════════════════════════════════════════════════ */

    const handleDietMealFoodChange = (e) => {
        const { name, value } = e.target;
        const [day, meal, index, field] = name.split('-');

        const updatedDietPlan = { ...dietPlan };
        const parsedValue = (value === '' || isNaN(value)) ? value : parseFloat(value);
        const foodItem = updatedDietPlan[day][meal].foods[index];
        const baseFoodItem = baseMacrosFoodProportion[day][meal].foods[index];

        if (field === 'quantity') {
            const newQuantity = parsedValue;
            if (newQuantity > 0) {
                const ratio = newQuantity / baseFoodItem.quantity;
                foodItem.quantity = newQuantity;
                PROPORTIONAL_MACROS.forEach((macro) => {
                    foodItem[macro] = baseFoodItem[macro] * ratio;
                });
            } else {
                foodItem.quantity = 0;
                PROPORTIONAL_MACROS.forEach((macro) => { foodItem[macro] = 0; });
            }
        } else {
            foodItem[field] = value;
            baseFoodItem[field] = value;
        }

        if ([...ALL_MACROS, 'quantity'].includes(field)) {
            ALL_MACROS.forEach((macro) => {
                updatedDietPlan[day][meal][macro] = updatedDietPlan[day][meal].foods.reduce(
                    (sum, f) => sum + (parseFloat(f[macro]) || 0), 0
                );
            });

            const dailyTotals = Object.fromEntries(ALL_MACROS.map((m) => [m, 0]));
            Object.keys(updatedDietPlan[day]).forEach((key) => {
                if (key === 'totals') return;
                ALL_MACROS.forEach((macro) => {
                    dailyTotals[macro] += parseFloat(updatedDietPlan[day][key][macro]) || 0;
                });
            });
            updatedDietPlan[day].totals = dailyTotals;
        }

        setDietPlan(updatedDietPlan);
        if (onDietChange) onDietChange({ ...editedDiet, dietPlan: updatedDietPlan });
    };

    /* ═════════════════════════════════════════════════════════════════
       ALTERNATIVE FOOD FIELD CHANGE
       Name encoding: "{day}-{meal}-{foodIndex}-alt-{altIndex}-{field}"
       ═════════════════════════════════════════════════════════════════ */

    const handleAlternativeFoodChange = (e) => {
        const { name, value } = e.target;
        // parts: [day, meal, foodIndex, "alt", altIndex, field]
        const parts     = name.split('-');
        const day       = parts[0];
        const meal      = parts[1];
        const foodIndex = Number(parts[2]);
        const altIndex  = Number(parts[4]);
        const field     = parts[5];

        const updatedDietPlan = { ...dietPlan };
        const altItem = updatedDietPlan[day][meal].foods[foodIndex].alternatives[altIndex];

        // Name field stays as string; numeric fields are parsed
        altItem[field] = field === 'name' ? value : (value === '' || isNaN(value) ? value : parseFloat(value));

        setDietPlan(updatedDietPlan);
        if (onDietChange) onDietChange({ ...editedDiet, dietPlan: updatedDietPlan });
    };

    /* ── Toggle per-food alternatives accordion ───────────────────── */

    const toggleFoodAlternatives = (day, meal, index) => {
        const key = `${day}-${meal}-${index}`;
        setExpandedFoods((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    /* ── Add / remove per-food alternatives ──────────────────────── */

    const handleAddAlternative = (day, meal, foodIndex) => {
        const blankAlt = { name: '', quantity: 0, kcal: 0, fats: 0, proteins: 0, carbs: 0, sugars: 0, fibers: 0, salt: 0 };
        const updatedDietPlan = { ...dietPlan };
        const food = updatedDietPlan[day][meal].foods[foodIndex];
        food.alternatives = [...(food.alternatives || []), blankAlt];
        setDietPlan(updatedDietPlan);
        if (onDietChange) onDietChange({ ...editedDiet, dietPlan: updatedDietPlan });
        // Auto-expand so new row is visible
        setExpandedFoods((prev) => new Set(prev).add(`${day}-${meal}-${foodIndex}`));
    };

    const handleRemoveAlternative = (day, meal, foodIndex, altIndex) => {
        const updatedDietPlan = { ...dietPlan };
        const food = updatedDietPlan[day][meal].foods[foodIndex];
        food.alternatives = food.alternatives.filter((_, i) => i !== altIndex);
        setDietPlan(updatedDietPlan);
        if (onDietChange) onDietChange({ ...editedDiet, dietPlan: updatedDietPlan });
    };

    /* ── Diet CRUD ────────────────────────────────────────────────── */

    const deleteDiet = async () => {
        setLoading(true);
        try {
            await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/diets/${diet._id}`,
                { withCredentials: true }
            );
        } catch (err) {
            console.error(err);
        } finally {
            await onDeleteDiet();
            setShowDeleteDietConfirmationModal(false);
            setLoading(false);
        }
    };

    const deleteMeal = (meal, day) => {
        const newDietPlan = { ...dietPlan };
        const dailyDietCopy = Object.fromEntries(
            Object.entries(dietPlan[day]).filter(([key]) => key !== meal)
        );
        newDietPlan[day] = dailyDietCopy;

        const dailyTotals = Object.fromEntries(PROPORTIONAL_MACROS.map((m) => [m, 0]));
        Object.keys(newDietPlan[day]).forEach((key) => {
            if (key === 'totals') return;
            PROPORTIONAL_MACROS.forEach((macro) => {
                dailyTotals[macro] += parseFloat(newDietPlan[day][key][macro]) || 0;
            });
        });
        newDietPlan[day].totals = dailyTotals;

        setDietPlan(newDietPlan);
        setBaseMacrosFoodProportion((prev) => ({ ...prev, [day]: dailyDietCopy }));
        if (onDietChange) onDietChange({ ...editedDiet, dietPlan: newDietPlan });
        setShowDeleteMealConfirmationModal(false);
    };

    const deleteFood = (day, meal, index) => {
        const foodListCopy = [...dietPlan[day][meal].foods];
        foodListCopy.splice(index, 1);

        if (foodListCopy.length === 0) {
            deleteMeal(meal, day);
            return;
        }

        const newDietPlan = { ...dietPlan };
        newDietPlan[day][meal].foods = foodListCopy;

        PROPORTIONAL_MACROS.forEach((macro) => {
            newDietPlan[day][meal][macro] = foodListCopy.reduce(
                (sum, f) => sum + (parseFloat(f[macro]) || 0), 0
            );
        });

        const dailyTotals = Object.fromEntries(PROPORTIONAL_MACROS.map((m) => [m, 0]));
        Object.keys(newDietPlan[day]).forEach((key) => {
            if (key === 'totals') return;
            PROPORTIONAL_MACROS.forEach((macro) => {
                dailyTotals[macro] += parseFloat(newDietPlan[day][key][macro]) || 0;
            });
        });
        newDietPlan[day].totals = dailyTotals;

        setDietPlan(newDietPlan);
        setBaseMacrosFoodProportion((prev) => ({
            ...prev,
            [day]: { ...prev[day], [meal]: { ...prev[day][meal], foods: foodListCopy } },
        }));
        if (onDietChange) onDietChange({ ...editedDiet, dietPlan: newDietPlan });
    };

    /* ── Meal options menu ────────────────────────────────────────── */

    const handleToggleMealOptionsMenu = (meal, event) => {
        setMenuAnchorEls((prev) => ({ ...prev, [meal]: event.currentTarget }));
    };

    const handleCloseMealOptionsMenu = (meal) => {
        setMenuAnchorEls((prev) => ({ ...prev, [meal]: null }));
    };

    const handleAddFoodToMeal = (day, meal) => {
        const blankFood = { name: '', quantity: 0, kcal: 0, fats: 0, proteins: 0, carbs: 0, alternatives: [] };
        const dailyFoods = [...dietPlan[day][meal].foods, blankFood];

        const updateFoods = (prev) => ({
            ...prev,
            [day]: { ...prev[day], [meal]: { ...prev[day][meal], foods: dailyFoods } },
        });

        setDietPlan(updateFoods);
        setBaseMacrosFoodProportion(updateFoods);

        if (onDietChange) {
            onDietChange({
                ...editedDiet,
                dietPlan: {
                    ...editedDiet.dietPlan,
                    [day]: {
                        ...editedDiet.dietPlan[day],
                        [meal]: { ...editedDiet.dietPlan[day][meal], foods: dailyFoods },
                    },
                },
            });
        }
        handleCloseMealOptionsMenu(meal);
    };

    const handleFoodOptionSelect = (event, value, day, meal, index) => {
        if (event.type !== 'click') return;

        const existingAlternatives = dietPlan[day][meal].foods[index]?.alternatives || [];
        const selectedFood = !value
            ? { name: '', quantity: 0, kcal: 0, fats: 0, proteins: 0, carbs: 0, alternatives: [] }
            : typeof value === 'object'
                ? { ...value, alternatives: existingAlternatives }
                : { ...(foodSearchList.find((f) => f.name[currentLanguage] === value) || {}), alternatives: existingAlternatives };

        const foodListCopy = [...dietPlan[day][meal].foods];
        foodListCopy[index] = selectedFood;

        const newDietPlan = { ...dietPlan };
        newDietPlan[day][meal].foods = foodListCopy;

        PROPORTIONAL_MACROS.forEach((macro) => {
            newDietPlan[day][meal][macro] = foodListCopy.reduce(
                (sum, f) => sum + (parseFloat(f[macro]) || 0), 0
            );
        });

        const dailyTotals = Object.fromEntries(PROPORTIONAL_MACROS.map((m) => [m, 0]));
        Object.keys(newDietPlan[day]).forEach((key) => {
            if (key === 'totals') return;
            PROPORTIONAL_MACROS.forEach((macro) => {
                dailyTotals[macro] += parseFloat(newDietPlan[day][key][macro]) || 0;
            });
        });
        newDietPlan[day].totals = dailyTotals;

        setDietPlan(newDietPlan);
        setBaseMacrosFoodProportion((prev) => ({
            ...prev,
            [day]: { ...prev[day], [meal]: { ...prev[day][meal], foods: foodListCopy } },
        }));
        if (onDietChange) onDietChange({ ...editedDiet, dietPlan: newDietPlan });
        setFoodSearchList([]);
    };

    /* ── Alternative food search ─────────────────────────────────── */

    const getAltFoodsFilteredByName = async (foodName) => {
        const url = `${process.env.REACT_APP_BACKEND_URL}/foods/?nameFilter=${foodName}&language=${currentLanguage}${onlyGenerics ? '&onlyGenerics=true' : ''}`;
        const response = await axios.get(url, { withCredentials: true });
        setAltFoodSearchList(response.data);
    };

    const handleAltFoodInputChange = (event, newInputValue, reason, day, meal, foodIndex, altIndex) => {
        if (reason !== 'input' && reason !== 'clear') return;
        if (!newInputValue) {
            setAltFoodSearchList([]);
        } else {
            getAltFoodsFilteredByName(newInputValue);
        }
    };

    const handleAltFoodOptionSelect = (event, value, day, meal, foodIndex, altIndex) => {
        if (event.type !== 'click') return;
        const updatedDietPlan = { ...dietPlan };
        const altItem = updatedDietPlan[day][meal].foods[foodIndex].alternatives[altIndex];

        if (!value) {
            altItem.name = ''; altItem.quantity = 0; altItem.kcal = 0;
            altItem.fats = 0; altItem.proteins = 0; altItem.carbs = 0;
            altItem.sugars = 0; altItem.fibers = 0; altItem.salt = 0;
        } else {
            const src = typeof value === 'object'
                ? value
                : altFoodSearchList.find((f) => f.name?.[currentLanguage] === value);
            if (src) {
                Object.assign(altItem, {
                    name: src.name,
                    quantity: src.quantity ?? 100,
                    kcal:     src.kcal     ?? 0,
                    fats:     src.fats     ?? 0,
                    proteins: src.proteins ?? 0,
                    carbs:    src.carbs    ?? 0,
                    sugars:   src.sugars   ?? 0,
                    fibers:   src.fibers   ?? 0,
                    salt:     src.salt     ?? 0,
                });
            }
        }

        setDietPlan(updatedDietPlan);
        if (onDietChange) onDietChange({ ...editedDiet, dietPlan: updatedDietPlan });
        setAltFoodSearchList([]);
    };

    /* ── PDF export ───────────────────────────────────────────────── */

    const handleDownloadAlternativesPDF = () => {
        const doc = new jsPDF();
        const pageHeight = doc.internal.pageSize.getHeight();
        const pageWidth  = doc.internal.pageSize.getWidth();
        let y = 25;
        const lineHeight = 8;

        const dietPreferredLanguage = diet?.dietLanguage ?? diet?.language ?? currentLanguage ?? 'en';
        const dietT = (key, options = {}) => t(key, { ...options, lng: dietPreferredLanguage });

        const addLine = (text, indent = 0) => {
            const lines = doc.splitTextToSize(text, 180 - indent);
            lines.forEach((line) => {
                if (y + lineHeight > pageHeight - 15) { doc.addPage(); y = 20; }
                doc.text(line, 10 + indent, y);
                y += lineHeight;
            });
        };

        const resolveName = (nameField) => {
            if (!nameField) return '';
            if (typeof nameField === 'string') return nameField;
            return nameField[dietPreferredLanguage] ?? nameField.en ?? Object.values(nameField)[0] ?? '';
        };

        doc.setFont('Helvetica', 'bold');
        doc.setFontSize(22);
        doc.text(capitalize(dietT('diet-plan')), 10, 15);
        doc.setFontSize(12);
        doc.setFont('Helvetica', 'normal');

        addLine(`${capitalize(dietT('client'))}: ${client.name} ${client.surname}`);
        addLine(`${capitalize(dietT('start-date'))}: ${new Date(diet.startDate).toLocaleDateString()}`);
        addLine(`${capitalize(dietT('end-date'))}: ${new Date(diet.endDate).toLocaleDateString()}`);

        const dayCount = Object.keys(diet.dietPlan).length;
        for (let dayIdx = 0; dayIdx < dayCount; dayIdx++) {
            const dayKey = `day${dayIdx}`;
            const dailyDiet = diet.dietPlan[dayKey];
            if (!dailyDiet) continue;

            addLine('');
            doc.setFont('Helvetica', 'bold');
            doc.setFontSize(18);
            const dayLabel = dayIdx === 0
                ? dietT('main-plan').toUpperCase()
                : dietT('alternative-label', { n: dayIdx }).toUpperCase();
            const textWidth = doc.getTextWidth(dayLabel);
            if (y + lineHeight > pageHeight - 15) { doc.addPage(); y = 20; }
            doc.text(dayLabel, (pageWidth - textWidth) / 2, y);
            y += lineHeight;
            doc.setFont('Helvetica', 'normal');
            doc.setFontSize(12);

            MEAL_ORDER.forEach((meal) => {
                if (!dailyDiet[meal]) return;
                addLine('');
                doc.setFont('Helvetica', 'bold');
                doc.setFontSize(14);
                addLine(dietT(meal).toUpperCase());
                doc.setFont('Helvetica', 'normal');
                doc.setFontSize(12);

                dailyDiet[meal].foods.forEach((food) => {
                    addLine(`${resolveName(food.name)} ${food.quantity}g`);

                    if (Array.isArray(food.alternatives) && food.alternatives.length > 0) {
                        doc.setFont('Helvetica', 'italic');
                        doc.setFontSize(10);
                        food.alternatives.forEach((alt, altIdx) => {
                            addLine(
                                `\u21b3 ${dietT('alternative-label', { n: altIdx + 1 })}: ${resolveName(alt.name)} ${alt.quantity}g`,
                                8
                            );
                        });
                        doc.setFont('Helvetica', 'normal');
                        doc.setFontSize(12);
                    }
                });
            });
        }

        doc.save(`${dietT('diet')}-alternatives-${client.name}-${client.surname}.pdf`);
    };

    /* ═══════════════════════════════════════════════════════════════
       RENDER HELPERS
       ═══════════════════════════════════════════════════════════════ */

    const renderNumericField = (fieldName, value, max = 9999, decimals = null) => (
        <TextField
            name={fieldName}
            type="number"
            value={!value ? '' : (decimals !== null ? Number(Number(value).toFixed(decimals)) : Number(value))}
            placeholder="0"
            disabled={!editing}
            onChange={(e) => {
                const clamped = Math.min(Number(e.target.value), max);
                handleDietMealFoodChange({ ...e, target: { ...e.target, value: clamped, name: e.target.name } });
            }}
            sx={numFieldSx}
        />
    );

    const renderAltNumericField = (fieldName, value, max = 9999, decimals = null) => (
        <TextField
            name={fieldName}
            type="number"
            value={!value ? '' : (decimals !== null ? Number(Number(value).toFixed(decimals)) : Number(value))}
            placeholder="0"
            disabled={!editing}
            onChange={(e) => {
                const clamped = Math.min(Number(e.target.value), max);
                handleAlternativeFoodChange({ ...e, target: { ...e.target, value: clamped, name: e.target.name } });
            }}
            sx={{ ...numFieldSx, width: '58px' }}
        />
    );

    const renderMealOptionsMenu = (meal, day) => (
        <div>
            <IconButton
                size="small"
                id={`adt-menu-btn-${meal}`}
                aria-controls={menuAnchorEls[meal] ? `adt-menu-${meal}` : undefined}
                aria-expanded={menuAnchorEls[meal] ? 'true' : undefined}
                aria-haspopup="true"
                onClick={(e) => handleToggleMealOptionsMenu(meal, e)}
            >
                <MoreVertIcon fontSize="small" />
            </IconButton>
            <Popper
                open={Boolean(menuAnchorEls[meal])}
                anchorEl={menuAnchorEls[meal]}
                placement="bottom-start"
                transition
                style={{ zIndex: 1000 }}
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{ transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom' }}
                    >
                        <Paper elevation={3} sx={{ backgroundColor: '#fff' }}>
                            <ClickAwayListener onClickAway={() => handleCloseMealOptionsMenu(meal)}>
                                <MenuList autoFocusItem={Boolean(menuAnchorEls[meal])} id={`adt-menu-${meal}`}>
                                    <MenuItem onClick={() => handleAddFoodToMeal(day, meal)}>
                                        <LunchDiningIcon fontSize="small" sx={{ mr: 1 }} />
                                        {capitalizeFirst(t('add-food'))}
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            setDeleteMealConfirmationModalOptions({ meal, day });
                                            setShowDeleteMealConfirmationModal(true);
                                            handleCloseMealOptionsMenu(meal);
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                                        {capitalizeFirst(t('delete-meal'))}
                                    </MenuItem>
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </div>
    );

    const fmtMacro = (val, decimals = 1) => (val != null ? Number(val.toFixed(decimals)) : 0);

    const resolveName = (nameField, lang) => {
        if (!nameField) return '';
        if (typeof nameField === 'string') return nameField;
        return nameField[lang] ?? nameField.en ?? Object.values(nameField)[0] ?? '';
    };

    /* ── Main table rows renderer ─────────────────────────────────── */

    const renderTableRows = () => {
        const day = 'day' + selectedDay;
        const rows = [];
        let firstMealRow = true;
        let firstExpandBtn = true;
        if (!dietPlan[day]) return rows;

        MEAL_ORDER.forEach((meal) => {
            if (!dietPlan[day][meal]) return;
            const mealData = dietPlan[day][meal];

            /* Meal header row */
            rows.push(
                <TableRow key={`${day}-${meal}`} className="adt-meal-row" {...(firstMealRow ? { 'data-tour': 'adt-meal-header' } : {})}>
                    <TableCell className="adt-cell">
                        <span className="adt-meal-name">{t(meal).toUpperCase()}</span>
                    </TableCell>
                    <TableCell className="adt-cell" align="right" />
                    <TableCell className="adt-cell adt-col-kcal" align="right">
                        <b>{Math.round(mealData.kcal ?? 0)}</b>
                    </TableCell>
                    <TableCell className="adt-cell" align="right">
                        <b>{fmtMacro(mealData.fats)}</b>
                    </TableCell>
                    <TableCell className="adt-cell" align="right">
                        <b>{fmtMacro(mealData.carbs)}</b>
                    </TableCell>
                    <TableCell className="adt-cell adt-col-detail" align="right">
                        <b>{fmtMacro(mealData.sugars)}</b>
                    </TableCell>
                    <TableCell className="adt-cell adt-col-detail" align="right">
                        <b>{fmtMacro(mealData.fibers)}</b>
                    </TableCell>
                    <TableCell className="adt-cell" align="right">
                        <b>{fmtMacro(mealData.proteins)}</b>
                    </TableCell>
                    <TableCell className="adt-cell adt-col-detail" align="right">
                        <b>{fmtMacro(mealData.salt)}</b>
                    </TableCell>
                    {editing && (
                        <TableCell className="adt-cell">
                            {renderMealOptionsMenu(meal, day)}
                        </TableCell>
                    )}
                    {/* Expand column spacer */}
                    <TableCell className="adt-cell" />
                </TableRow>
            );
            firstMealRow = false;

            /* Food rows */
            mealData.foods.forEach((food, index) => {
                const namePrefix = `${day}-${meal}-${index}`;
                const expandKey  = `${day}-${meal}-${index}`;
                const isExpanded = expandedFoods.has(expandKey);
                const alts       = food.alternatives || [];

                rows.push(
                    <TableRow key={namePrefix} className="adt-food-row">
                        {/* Food name */}
                        <TableCell className="adt-cell">
                            <Autocomplete
                                id={`${namePrefix}-name`}
                                className="adt-food-name-input"
                                value={resolveName(food.name, currentLanguage)}
                                freeSolo
                                disableClearable={!editing}
                                disabled={!editing}
                                onChange={(e, val) => handleFoodOptionSelect(e, val, day, meal, index)}
                                options={foodSearchList.filter((f) => f.name?.[currentLanguage])}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    return option.name?.[currentLanguage] || '';
                                }}
                                onInputChange={(event, newInputValue, reason) =>
                                    handleFoodInputChange(event, newInputValue, reason, day, meal, index)
                                }
                                renderOption={(props, option) => {
                                    const name = option.name?.[currentLanguage] || '';
                                    const brand = option.brand;
                                    const brandDisplay = brand
                                        ? (typeof brand === 'object' ? (brand?.[currentLanguage] ?? brand?.en ?? '') : brand)
                                        : null;
                                    return (
                                        <li {...props} key={option._id}>
                                            {name}
                                            {brandDisplay && (
                                                <span style={{ marginLeft: 8, fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic' }}>
                                                    {brandDisplay}
                                                </span>
                                            )}
                                        </li>
                                    );
                                }}
                                renderInput={(params) => <TextField {...params} size="small" />}
                                filterOptions={(x) => x}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover fieldset': { borderColor: appColors.tertiary },
                                        '&.Mui-focused fieldset': { borderColor: appColors.secondary },
                                    },
                                }}
                            />
                        </TableCell>

                        {/* Macros */}
                        <TableCell className="adt-cell" align="right">
                            {renderNumericField(`${namePrefix}-quantity`, food.quantity)}
                        </TableCell>
                        <TableCell className="adt-cell adt-col-kcal" align="right">
                            {renderNumericField(`${namePrefix}-kcal`, food.kcal, 99999)}
                        </TableCell>
                        <TableCell className="adt-cell" align="right">
                            {renderNumericField(`${namePrefix}-fats`, food.fats, 9999, 1)}
                        </TableCell>
                        <TableCell className="adt-cell" align="right">
                            {renderNumericField(`${namePrefix}-carbs`, food.carbs, 9999, 1)}
                        </TableCell>
                        <TableCell className="adt-cell adt-col-detail" align="right">
                            {renderNumericField(`${namePrefix}-sugars`, food.sugars, 9999, 1)}
                        </TableCell>
                        <TableCell className="adt-cell adt-col-detail" align="right">
                            {renderNumericField(`${namePrefix}-fibers`, food.fibers, 9999, 1)}
                        </TableCell>
                        <TableCell className="adt-cell" align="right">
                            {renderNumericField(`${namePrefix}-proteins`, food.proteins, 9999, 1)}
                        </TableCell>
                        <TableCell className="adt-cell adt-col-detail" align="right">
                            {renderNumericField(`${namePrefix}-salt`, food.salt, 9999, 1)}
                        </TableCell>

                        {/* Delete button (edit mode) */}
                        {editing && (
                            <TableCell className="adt-cell">
                                <div className="adt-delete-btn" onClick={() => deleteFood(day, meal, index)}>
                                    <DeleteIcon fontSize="small" />
                                </div>
                            </TableCell>
                        )}

                        {/* Expand toggle for alternatives */}
                        <TableCell className="adt-cell" align="center">
                            {(alts.length > 0 || editing) && (
                                <Tooltip title={t('food-alternatives')}>
                                    <IconButton
                                        size="small"
                                        data-tour={firstExpandBtn ? 'adt-expand-btn' : undefined}
                                        onClick={() => toggleFoodAlternatives(day, meal, index)}
                                    >
                                        {isExpanded
                                            ? <ExpandLessIcon fontSize="small" />
                                            : <ExpandMoreIcon fontSize="small" />}
                                    </IconButton>
                                </Tooltip>
                            )}
                        </TableCell>
                    </TableRow>
                );
                if (firstExpandBtn) firstExpandBtn = false;

                /* Alternative sub-rows */
                if (isExpanded) {
                    alts.forEach((alt, altIdx) => {
                        const altPrefix = `${day}-${meal}-${index}-alt-${altIdx}`;
                        rows.push(
                            <TableRow key={altPrefix} className="adt-alt-row">
                                {/* Alternative name */}
                                <TableCell className="adt-cell adt-alt-name-cell">
                                    <span className="adt-alt-indicator">↳</span>
                                    {editing ? (
                                        <Autocomplete
                                            size="small"
                                            freeSolo
                                            value={resolveName(alt.name, currentLanguage)}
                                            options={altFoodSearchList.filter((f) => f.name?.[currentLanguage])}
                                            getOptionLabel={(option) =>
                                                typeof option === 'string' ? option : (option.name?.[currentLanguage] || '')
                                            }
                                            onInputChange={(event, newInputValue, reason) =>
                                                handleAltFoodInputChange(event, newInputValue, reason, day, meal, index, altIdx)
                                            }
                                            onChange={(e, val) => handleAltFoodOptionSelect(e, val, day, meal, index, altIdx)}
                                            renderOption={(props, option) => {
                                                const name = option.name?.[currentLanguage] || '';
                                                const brand = option.brand;
                                                const brandDisplay = brand
                                                    ? (typeof brand === 'object' ? (brand?.[currentLanguage] ?? brand?.en ?? '') : brand)
                                                    : null;
                                                return (
                                                    <li {...props} key={option._id}>
                                                        {name}
                                                        {brandDisplay && (
                                                            <span style={{ marginLeft: 8, fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic' }}>
                                                                {brandDisplay}
                                                            </span>
                                                        )}
                                                    </li>
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    size="small"
                                                    sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem', padding: '3px 8px' } }}
                                                />
                                            )}
                                            filterOptions={(x) => x}
                                            sx={{
                                                flex: 1,
                                                '& .MuiOutlinedInput-root': {
                                                    '&:hover fieldset': { borderColor: appColors.tertiary },
                                                    '&.Mui-focused fieldset': { borderColor: appColors.secondary },
                                                },
                                            }}
                                        />
                                    ) : (
                                        <span className="adt-alt-name">{resolveName(alt.name, currentLanguage)}</span>
                                    )}
                                </TableCell>

                                {/* Alt macros */}
                                <TableCell className="adt-cell" align="right">
                                    {renderAltNumericField(`${altPrefix}-quantity`, alt.quantity)}
                                </TableCell>
                                <TableCell className="adt-cell adt-col-kcal" align="right">
                                    {renderAltNumericField(`${altPrefix}-kcal`, alt.kcal, 99999)}
                                </TableCell>
                                <TableCell className="adt-cell" align="right">
                                    {renderAltNumericField(`${altPrefix}-fats`, alt.fats, 9999, 1)}
                                </TableCell>
                                <TableCell className="adt-cell" align="right">
                                    {renderAltNumericField(`${altPrefix}-carbs`, alt.carbs, 9999, 1)}
                                </TableCell>
                                <TableCell className="adt-cell adt-col-detail" align="right">
                                    {renderAltNumericField(`${altPrefix}-sugars`, alt.sugars, 9999, 1)}
                                </TableCell>
                                <TableCell className="adt-cell adt-col-detail" align="right">
                                    {renderAltNumericField(`${altPrefix}-fibers`, alt.fibers, 9999, 1)}
                                </TableCell>
                                <TableCell className="adt-cell" align="right">
                                    {renderAltNumericField(`${altPrefix}-proteins`, alt.proteins, 9999, 1)}
                                </TableCell>
                                <TableCell className="adt-cell adt-col-detail" align="right">
                                    {renderAltNumericField(`${altPrefix}-salt`, alt.salt, 9999, 1)}
                                </TableCell>

                                {/* Delete alternative */}
                                {editing && (
                                    <TableCell className="adt-cell">
                                        <div
                                            className="adt-delete-btn"
                                            onClick={() => handleRemoveAlternative(day, meal, index, altIdx)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </div>
                                    </TableCell>
                                )}
                                <TableCell className="adt-cell" />
                            </TableRow>
                        );
                    });

                    /* Add alternative button (edit mode only) */
                    if (editing) {
                        rows.push(
                            <TableRow key={`${day}-${meal}-${index}-add-alt`} className="adt-add-alt-row">
                                <TableCell colSpan={editing ? 11 : 10} className="adt-cell">
                                    <Button
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() => handleAddAlternative(day, meal, index)}
                                        sx={{
                                            ml: 3,
                                            fontSize: '0.75rem',
                                            textTransform: 'none',
                                            color: appColors.secondary,
                                        }}
                                    >
                                        {capitalizeFirst(t('add-alternative'))}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        );
                    }
                }
            });
        });

        return rows;
    };

    /* ═══════════════════════════════════════════════════════════════
       DAY NAVIGATION LABELS
       ═══════════════════════════════════════════════════════════════ */

    const dayCount  = diet?.dietPlan ? Object.keys(diet.dietPlan).length : 0;
    const dayLabels = Array.from({ length: dayCount }, (_, i) =>
        i === 0 ? t('main-plan') : t('alternative-label', { n: i })
    );

    const dayKey = 'day' + selectedDay;
    const totals = dietPlan[dayKey]?.totals;

    /* ═══════════════════════════════════════════════════════════════
       JSX
       ═══════════════════════════════════════════════════════════════ */

    return (
        <>
            <LoadingOverlay open={loading} />

            <div className="adt-wrapper">
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <TourButton steps={tourSteps} iconOnly={isMobile} label={t('tour.tutorial-btn')} />
                </div>
                {/* ── Info bar ──────────────────────────────────────── */}
                {showInfoBar && (
                    <div className="adt-info" data-tour="adt-info">
                        {dietGeneration ? (
                            <div className="adt-info-dates">
                                <p>
                                    <b>{t('start-date')}:</b>{' '}
                                    {dietGeneration.startDate
                                        ? formatDate(getFullDate(dietGeneration.startDate), 'dd-mm-yyyy')
                                        : '-'}
                                </p>
                                <p>
                                    <b>{t('end-date')}:</b>{' '}
                                    {dietGeneration.endDate
                                        ? formatDate(getFullDate(dietGeneration.endDate), 'dd-mm-yyyy')
                                        : '-'}
                                </p>
                                {dietGeneration.dietType && (
                                    <p>
                                        <b>{capitalizeFirst(t('diet-type'))}:</b>{' '}
                                        {capitalizeFirst(t(dietGeneration.dietType))}
                                    </p>
                                )}
                            </div>
                        ) : !editing && (
                            <>
                                <div className="adt-info-dates">
                                    <p>
                                        <b>{t('start-date')}:</b>{' '}
                                        {diet?.startDate
                                            ? formatDate(getFullDate(diet.startDate), 'dd-mm-yyyy')
                                            : '-'}
                                    </p>
                                    <p>
                                        <b>{t('end-date')}:</b>{' '}
                                        {diet?.endDate
                                            ? formatDate(getFullDate(diet.endDate), 'dd-mm-yyyy')
                                            : '-'}
                                    </p>
                                        {diet?.dietType && (
                                        <p>
                                            <b>{capitalizeFirst(t('diet-type'))}:</b>{' '}
                                            {capitalizeFirst(t(diet?.dietType))}
                                        </p>
                                    )}
                                </div>
                                <div className="adt-info-actions">
                                    <Tooltip title={t('Edit')}>
                                        <div className="adt-action-btn" onClick={() => setShowEditDietModal(true)}>
                                            <EditNoteIcon fontSize="small" />
                                            <p>{t('Edit')}</p>
                                        </div>
                                    </Tooltip>
                                    <Tooltip title={t('Download')}>
                                        <div className="adt-action-btn" onClick={handleDownloadAlternativesPDF}>
                                            <DownloadIcon fontSize="small" />
                                            <p>{t('Download')}</p>
                                        </div>
                                    </Tooltip>
                                    <Tooltip title={capitalize(t('delete'))}>
                                        <div className="adt-action-btn" onClick={() => setShowDeleteDietConfirmationModal(true)}>
                                            <DeleteIcon fontSize="small" />
                                            <p>{capitalize(t('delete'))}</p>
                                        </div>
                                    </Tooltip>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* ── Day selector ──────────────────────────────────── */}
                <div className="adt-day-selector" data-tour="adt-day-selector">
                    <button
                        className="adt-nav-btn"
                        disabled={selectedDay === 0 || !!dietGeneration}
                        onClick={() => handleDayChange(selectedDay - 1)}
                    >
                        <ChevronLeftIcon />
                    </button>

                    <ButtonGroup variant="outlined" disabled={!!dietGeneration}>
                        {dayLabels.map((label, i) => (
                            <Button
                                key={i}
                                onClick={() => handleDayChange(i)}
                                className={`adt-day-btn ${selectedDay === i ? 'adt-day-btn--active' : ''}`}
                            >
                                {label}
                            </Button>
                        ))}
                    </ButtonGroup>

                    <button
                        className="adt-nav-btn"
                        disabled={selectedDay === dayCount - 1 || !!dietGeneration}
                        onClick={() => handleDayChange(selectedDay + 1)}
                    >
                        <ChevronRightIcon />
                    </button>
                </div>

                {/* ── Generic brand filter (edit mode) ─────────────── */}
                {editing && (
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 1, pb: 1 }}>
                        <Tooltip title={t('only-generic-brand-info')} arrow placement="top">
                            <InfoOutlinedIcon sx={{ fontSize: '1rem', color: '#94a3b8', mr: 0.5, cursor: 'default' }} />
                        </Tooltip>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={onlyGenerics}
                                    onChange={(e) => {
                                        setOnlyGenerics(e.target.checked);
                                        setFoodSearchList([]);
                                    }}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: appColors.secondary },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: appColors.secondary },
                                    }}
                                />
                            }
                            label={<span style={{ fontSize: '0.85rem' }}>{t('only-generic-brand')}</span>}
                            sx={{ m: 0 }}
                        />
                    </Box>
                )}

                {/* ── Table ─────────────────────────────────────────── */}
                <TableContainer component={Paper} className="adt-table-container" data-tour="adt-table-container">
                    <Table size="small" aria-label="alternatives diet table">
                        <TableHead>
                            <TableRow>
                                <TableCell className="adt-header-cell" sx={{ minWidth: 200 }}>
                                    {editing && (
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            size="small"
                                            onClick={() => setShowAddMealModal(true)}
                                            sx={{
                                                backgroundColor: appColors.primary,
                                                fontSize: '0.75rem',
                                                textTransform: 'none',
                                                ':hover': { backgroundColor: appColors.secondary },
                                            }}
                                        >
                                            {t('add-new-meal')}
                                        </Button>
                                    )}
                                </TableCell>
                                <TableCell className="adt-header-cell" align="right">
                                    {t('Quantity').toUpperCase()}
                                    <br /><span style={{ fontSize: '0.7rem' }}>(gr/ml)</span>
                                </TableCell>
                                <TableCell className="adt-header-cell adt-col-kcal" align="right">
                                    {t('kcal').toUpperCase()}
                                </TableCell>
                                <TableCell className="adt-header-cell" align="right">
                                    {t('Fats').toUpperCase()}
                                </TableCell>
                                <TableCell className="adt-header-cell" align="right">
                                    {t('Carbs').toUpperCase()}
                                </TableCell>
                                <TableCell className="adt-header-cell adt-col-detail" align="right">
                                    {t('sugars').toUpperCase()}
                                </TableCell>
                                <TableCell className="adt-header-cell adt-col-detail" align="right">
                                    {t('fibers').toUpperCase()}
                                </TableCell>
                                <TableCell className="adt-header-cell" align="right">
                                    {t('Proteins').toUpperCase()}
                                </TableCell>
                                <TableCell className="adt-header-cell adt-col-detail" align="right">
                                    {t('salt').toUpperCase()}
                                </TableCell>
                                {editing && <TableCell className="adt-header-cell" />}
                                {/* Expand column */}
                                <TableCell className="adt-header-cell" sx={{ width: 40 }} />
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {dietGeneration ? (
                                <TableRow>
                                    <TableCell colSpan={editing ? 11 : 10} align="center" sx={{ border: 0, py: 4 }}>
                                        <Box sx={{ width: '80%', mx: 'auto', mb: 1 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={dietGeneration.progress ?? 0}
                                                sx={{
                                                    backgroundColor: appColors.tertiary,
                                                    height: 20,
                                                    borderRadius: 5,
                                                    '& .MuiLinearProgress-bar': {
                                                        backgroundColor: appColors.secondary,
                                                        borderRadius: 5,
                                                    },
                                                }}
                                            />
                                        </Box>
                                        <span>
                                            {t('generating-diet')}...{' '}
                                            {dietGeneration.progress
                                                ? `${Math.round(dietGeneration.progress)}%`
                                                : '0%'}
                                        </span>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <>
                                    {/* Daily total row */}
                                    <TableRow className="adt-total-row" data-tour="adt-total-row">
                                        <TableCell className="adt-cell"><b>{t('Total')}</b></TableCell>
                                        <TableCell className="adt-cell" align="right" />
                                        <TableCell className="adt-cell adt-col-kcal" align="right">
                                            <b>{totals ? Math.round(totals.kcal) : 0}</b>
                                        </TableCell>
                                        <TableCell className="adt-cell" align="right">
                                            <b>{totals ? fmtMacro(totals.fats) : 0}</b>
                                        </TableCell>
                                        <TableCell className="adt-cell" align="right">
                                            <b>{totals ? fmtMacro(totals.carbs) : 0}</b>
                                        </TableCell>
                                        <TableCell className="adt-cell adt-col-detail" align="right">
                                            <b>{totals ? fmtMacro(totals.sugars) : 0}</b>
                                        </TableCell>
                                        <TableCell className="adt-cell adt-col-detail" align="right">
                                            <b>{totals ? fmtMacro(totals.fibers) : 0}</b>
                                        </TableCell>
                                        <TableCell className="adt-cell" align="right">
                                            <b>{totals ? fmtMacro(totals.proteins) : 0}</b>
                                        </TableCell>
                                        <TableCell className="adt-cell adt-col-detail" align="right">
                                            <b>{totals ? fmtMacro(totals.salt) : 0}</b>
                                        </TableCell>
                                        {editing && <TableCell className="adt-cell" />}
                                        <TableCell className="adt-cell" />
                                    </TableRow>
                                    {renderTableRows()}
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>

            {/* ── Modals ──────────────────────────────────────────── */}
            {showEditDietModal && (
                <EditDietModal
                    setShowModal={setShowEditDietModal}
                    diet={diet}
                    onSave={refreshDietTable}
                />
            )}
            {showAddMealModal && (
                <AddMealModal
                    setShowModal={setShowAddMealModal}
                    dietPlan={dietPlan}
                    setDietPlan={setDietPlan}
                    selectedDay={'day' + selectedDay}
                    setOriginalDietFoodProportion={setBaseMacrosFoodProportion}
                    handleEditedDiet={onDietChange}
                    editedDiet={editedDiet}
                />
            )}
            {showDeleteDietConfirmationModal && (
                <ConfirmationModal
                    id="delete-diet-confirmation-modal"
                    titleText={capitalize(t('delete-diet'))}
                    bodyText={capitalize(t('confirm-delete-diet'))}
                    handleConfirm={deleteDiet}
                    handleClose={() => setShowDeleteDietConfirmationModal(false)}
                />
            )}
            {showDeleteMealConfirmationModal && (
                <ConfirmationModal
                    id="delete-meal-confirmation-modal"
                    titleText={`${t('delete-meal')}: ${t(deleteMealConfirmationModalOptions.meal)}`}
                    bodyText={t('confirm-delete-meal')}
                    handleConfirm={() =>
                        deleteMeal(
                            deleteMealConfirmationModalOptions.meal,
                            deleteMealConfirmationModalOptions.day
                        )
                    }
                    handleClose={() => setShowDeleteMealConfirmationModal(false)}
                />
            )}
        </>
    );
};

export default AlternativesDietTable;
